import React, { useState, useEffect } from 'react';

// Pricing per model (per token)
const PRICING = {
  haiku:  { input: 0.80 / 1_000_000, output: 4.00 / 1_000_000 },   // Claude Haiku 4.5
  openai: { input: 2.00 / 1_000_000, output: 8.00 / 1_000_000 },   // GPT default
};

function estimateCost(promptTokens, generationTokens, externalApp) {
  const pricing = externalApp === 'monitor' ? PRICING.haiku : PRICING.openai;
  return (promptTokens || 0) * pricing.input + (generationTokens || 0) * pricing.output;
}

function formatCost(cost) {
  return '$' + cost.toFixed(4);
}

function Usage({ token }) {
  const [usageRecords, setUsageRecords] = useState([]);
  const [stats, setStats] = useState({ username: '', dailyTokens: 0, monthlyTokens: 0, totalTokens: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUsage = fetch('/api/usage', {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(res => res.json());

    const fetchStats = fetch('/api/usage/stats', {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(res => res.json());

    Promise.all([fetchUsage, fetchStats])
      .then(([usageData, statsData]) => {
        setUsageRecords(usageData);
        setStats(statsData);
        setIsLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setIsLoading(false);
      });
  }, [token]);

  if (isLoading) return <div>Loading usage data...</div>;
  if (error) return <div className="alert alert-danger">Error: {error}</div>;

  return (
    <div className="card shadow-sm mt-4">
      <div className="card-header bg-info text-white d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Usage History</h5>
        {stats.username && <span className="badge bg-light text-info">{stats.username}</span>}
      </div>
      <div className="card-body">
        <div className="row mb-4 text-center">
          <div className="col-md-3">
            <div className="border rounded p-3 bg-light">
              <h6 className="text-muted mb-1">Last 24 Hours</h6>
              <h4 className="mb-0">{stats.dailyTokens.toLocaleString()}</h4>
            </div>
          </div>
          <div className="col-md-3">
            <div className="border rounded p-3 bg-light">
              <h6 className="text-muted mb-1">Last 30 Days</h6>
              <h4 className="mb-0">{stats.monthlyTokens.toLocaleString()}</h4>
            </div>
          </div>
          <div className="col-md-3">
            <div className="border rounded p-3 bg-light">
              <h6 className="text-muted mb-1">Total Lifetime</h6>
              <h4 className="mb-0">{stats.totalTokens.toLocaleString()}</h4>
            </div>
          </div>
          <div className="col-md-3">
            <div className="border rounded p-3 bg-light">
              <h6 className="text-muted mb-1">Est. Total Cost</h6>
              <h4 className="mb-0">{formatCost(usageRecords.reduce((sum, r) => sum + estimateCost(r.promptTokens, r.generationTokens, r.externalApplication), 0))}</h4>
            </div>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>User</th>
                <th>Date</th>
                <th>Chat ID</th>
                <th>Ext. App</th>
                <th>Ext. ID</th>
                <th>Prompt Tokens</th>
                <th>Generation Tokens</th>
                <th>Total Tokens</th>
                <th>Cost (USD)</th>
              </tr>
            </thead>
            <tbody>
              {usageRecords.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center">No usage records found.</td>
                </tr>
              ) : (
                usageRecords.map((record) => (
                  <tr key={record.id}>
                    <td>{record.username}</td>
                    <td>{new Date(record.timestamp).toLocaleString()}</td>
                    <td>{record.chatId}</td>
                    <td>{record.externalApplication || '-'}</td>
                    <td>{record.externalId || '-'}</td>
                    <td>{record.promptTokens}</td>
                    <td>{record.generationTokens}</td>
                    <td>{record.totalTokens}</td>
                    <td>{formatCost(estimateCost(record.promptTokens, record.generationTokens, record.externalApplication))}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Usage;
